import type { HouseholdMember, HouseholdPetBrief } from "@/shared/contracts";
import { Icon, PetAvatar, Sheet } from "@/shared/ui";
import "./familyGroup.css";

/**
 * 通讯器「宠物的手机」里的家庭群（P2，用户 10-03 06:3x：「更像是一个家庭的群，就是几只宠物加邀请的好友或者加家人的这种感觉」）。
 * 只收 props，取数在 useFamilyGroup；接线归 6c2b。邀请好友进群今天不做（后端没有）。
 * 看得见的字与读屏里不写 AI、模型、生成和内部代码，不显示 user_id。
 */

type GroupPet = Pick<HouseholdPetBrief, "pet_id" | "name" | "species" | "photo_url" | "origin">;
type GroupPerson = Pick<HouseholdMember, "user_id" | "display_name" | "role" | "is_you">;

/** 群头像最多画几格：超过 4 只时前 3 只加一格「+N」 */
const MAX_CELLS = 4;
/** 格与格之间留的纸色缝（px），与样式里的 gap 一致 */
const GAP = 2;

/** 副标题「N 只伙伴 · M 位家人」：没有家人资料只写伙伴；一只伙伴都没有整句不写（6c2b 05:5x） */
export function familyGroupCountText(petCount: number, people: readonly unknown[] | null): string | null {
  if (petCount <= 0) return null;
  return people ? `${petCount} 只伙伴 · ${people.length} 位家人` : `${petCount} 只伙伴`;
}

/** 群头像的读屏名：四只以内全念，再多念前三只「等 N 只」 */
function avatarLabel(pets: readonly GroupPet[]): string {
  if (!pets.length) return "家庭来信";
  const names = pets.length > MAX_CELLS ? `${pets.slice(0, 3).map((p) => p.name).join("、")}等 ${pets.length} 只` : pets.map((p) => p.name).join("、");
  return `家庭来信：${names}`;
}

/**
 * 群头像：几只宠物拼起来。1 只整张；2 只左右；3、4 只田字格（3 只时第四格是空格子）；超过 4 只前 3 只加一格「+N」。
 * 每格用现成的 PetAvatar（照片 → 物种插画 → 爪印的退回链），不写名字首字。圆角方形、纸色细边。
 */
export function FamilyGroupAvatar({ pets, size = 44 }: { pets: readonly GroupPet[]; size?: number }) {
  // UI-DECLUTTER-01：还读不到伙伴（演示、首次读取前）不画一块空方块，放一个信封
  if (!pets.length) {
    return (
      <span className="ps-fg-avatar is-1 is-letter" style={{ width: size, height: size }} role="img" aria-label={avatarLabel(pets)}>
        <span className="ps-fg-avatar__cell" aria-hidden="true">
          <Icon name="mail" size={Math.round(size * 0.5)} />
        </span>
      </span>
    );
  }
  const overflow = pets.length > MAX_CELLS;
  const shown = overflow ? pets.slice(0, 3) : pets.slice(0, MAX_CELLS);
  const layout = pets.length <= 1 ? 1 : pets.length === 2 ? 2 : 4;
  const inner = size - 4; // 两边各 2px 纸色细边
  const cell = layout === 1 ? inner : Math.floor((inner - GAP) / 2);
  // 左右两格是竖条：头像按整高画、居中裁两边；田字格每格正方
  const avatarSize = layout === 2 ? inner : cell;
  return (
    <span className={`ps-fg-avatar is-${layout}`} style={{ width: size, height: size }} role="img" aria-label={avatarLabel(pets)}>
      {shown.map((pet) => (
        <span key={pet.pet_id} className="ps-fg-avatar__cell" aria-hidden="true">
          <PetAvatar petId={pet.pet_id} name={pet.name} species={pet.species} photoUrl={pet.photo_url} origin={pet.origin} size={avatarSize} />
        </span>
      ))}
      {overflow ? (
        <span className="ps-fg-avatar__cell ps-fg-avatar__more" aria-hidden="true">
          +{pets.length - 3}
        </span>
      ) : layout === 4 && shown.length === 3 ? (
        // 3 只时第四格补一个空格子：底色同其他格，不露出纸色底（深色模式下纸色是一块亮方块）
        <span className="ps-fg-avatar__cell is-empty" aria-hidden="true" />
      ) : null}
    </span>
  );
}

/** 群聊页顶上那一块：群头像、家名、「N 只伙伴 · M 位家人」（点开成员面板） */
export function FamilyGroupHeader({ householdName, pets, people, onOpenMembers }: {
  householdName: string | null;
  pets: readonly GroupPet[];
  people: readonly GroupPerson[] | null;
  onOpenMembers: () => void;
}) {
  const count = familyGroupCountText(pets.length, people);
  return (
    <div className="ps-fg-header">
      <FamilyGroupAvatar pets={pets} size={44} />
      <div className="ps-fg-header__text">
        <h2 className="ps-fg-header__title">{householdName ?? "家庭来信"}</h2>
        {count ? (
          <button type="button" className="ps-fg-header__count" onClick={onOpenMembers} aria-label={`${count}，看看家里都有谁`}>
            {count}
          </button>
        ) : null}
      </div>
    </div>
  );
}

/** 成员面板：先伙伴（头像＋名字），再家人（名字；自己标「我」，管理员标「管理员」）。没有家人资料只列伙伴，不报错 */
export function FamilyGroupMembers({ pets, people, onClose }: {
  pets: readonly GroupPet[];
  people: readonly GroupPerson[] | null;
  onClose: () => void;
}) {
  return (
    <Sheet title="家庭成员" subtitle={familyGroupCountText(pets.length, people) ?? undefined} onClose={onClose} className="ps-fg-members">
      {pets.length ? (
        <section className="ps-fg-members__group" aria-label="伙伴">
          <h3 className="ps-fg-members__heading">伙伴</h3>
          <ul className="ps-fg-members__list">
            {pets.map((pet) => (
              <li key={pet.pet_id} className="ps-fg-members__row">
                <PetAvatar petId={pet.pet_id} name={pet.name} species={pet.species} photoUrl={pet.photo_url} origin={pet.origin} size={40} />
                <span className="ps-fg-members__name">{pet.name}</span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      {people?.length ? (
        <section className="ps-fg-members__group" aria-label="家人">
          <h3 className="ps-fg-members__heading">家人</h3>
          <ul className="ps-fg-members__list">
            {people.map((person) => (
              <li key={person.user_id} className="ps-fg-members__row">
                <span className="ps-fg-members__name">{person.display_name.trim() || "家人"}</span>
                {person.is_you ? <span className="ps-fg-members__tag">我</span> : null}
                {person.role === "admin" ? <span className="ps-fg-members__tag">管理员</span> : null}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </Sheet>
  );
}
